import { CHARTER_COLORS as theme } from "../../shared/controleTheme";
import { ControlPointCard, PointMultiComments, PointTwoComment } from "../ClassicControlCards";
import { ControleAttachments } from "../ControleAttachments";
import {
    controleTabBadgeStyle,
    controleTabButtonStyle,
    controleTabsStyle,
    inputStyle,
    printReportButtonStyle,
    saveDossierButtonStyle,
    sectionTitleMarkerStyle,
    sectionTitleStyle,
    validateDossierButtonStyle,
} from "../classicStyles";
import { PATIENT_CONTROL_INSTRUCTIONS, SECTIONS, VARIANT } from "../classicVariant";
import { renderDossierList } from "./DossierListView";
import { renderPatientControleTab } from "./PatientControleTabView";
import { BlockingPatientIssuesForPoint } from "../../shared/components/BlockingPatientIssuesForPoint";
import { ControlPointInstruction } from "../../shared/components/ControlPointInstruction";
import { PointComment } from "../../shared/components/PointComment";
import { controleModeLabel, isAppleKeyboard, saveShortcutLabel } from "../../shared/controleConstants";
import { formatDate } from "../../shared/controleFormat";
import { controlPointColor, controlPointLabel, controlPointNumber } from "../../shared/controleVariants";
import { ExtractionHpReport } from "../../shared/reports/ExtractionHpReport";
import { LotReleaseReport } from "../../shared/reports/LotReleaseReport";
import { NonConformityReport } from "../../shared/reports/NonConformityReport";
import { ProductionControlReport } from "../../shared/reports/ProductionControlReport";
import { YieldReport } from "../../shared/reports/YieldReport";
import { backButtonStyle, controleTabsRowStyle, shortcutPillStyle } from "../../shared/styles/layoutStyles";

// Commentaires simples des points de la check-list : clé enregistrée dans formData.commentaires et intitulé.
const POINT_COMMENTS = {
    fspMduHp: { key: "fspMduHpEureka", title: "Sur Eureka : Vérification des FSP et MDU et ce qui apparaît en HP:" },
    besoinsRemplissage: { key: "besoinsRemplissageCassette", title: "Importer tableau des Besoins de Remplissage cassette:" },
    ruptures: {
        key: "rupturesMedicament",
        title: "Validation des Ruptures de Médicament : S'assurer que toutes les ruptures de médicaments sont documentées et validées:",
    },
    sortiesCassettes: { key: "sortiesMedicamentsArretesRompus", title: "Sorties des Médicaments Arrêtés / Rompus des cassettes:" },
    retraitsLots: { key: "validationRetraitsLots", title: "Validation des Retraits de Lots:" },
};

const RAPPELS_PRODUCTION_FIELDS = [
    { key: "validationRappelProductionPda", title: "Validation des Rappel production PDA (cf tableau ehpad):" },
    { key: "validationPatientsAbsents", title: "Validation des patients décédés/ hospitalisé/vacances:" },
    { key: "validationArretModificationsTraitements", title: "Validation arrêt et modifications de traitements" },
];

const PATIENT_POINT_KEYS = ["tracageLogiciels", "datesProduction", "posologies"];

const REPORT_SECTIONS = ["nonConformites", "controleProduction", "liberationLot", "rendement", "extractionHp"];

function ClassicDossierHeader({ view }) {
    const { activeControleMode, saveDossier, updateMeta, workspace } = view;
    return (
        <div style={{ background: "white", border: `1px solid ${theme.border}`, borderRadius: "8px", padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <button
                        type="button"
                        onClick={() => workspace.setActiveDossier(null)}
                        style={backButtonStyle}
                        onMouseEnter={e => {
                            e.currentTarget.style.boxShadow = "0 8px 18px rgba(12, 59, 60, 0.16)";
                            e.currentTarget.style.borderColor = theme.muted;
                            e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.boxShadow = "0 1px 2px rgba(12, 59, 60, 0.08)";
                            e.currentTarget.style.borderColor = theme.borderStrong;
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        Retour aux fiches
                    </button>
                    <div style={{ minWidth: 0 }}>
                        <h2 style={{ margin: 0, fontSize: "18px", color: theme.ink }}>Dossier de lot {workspace.activeDossier.numeroLot}</h2>
                        <div style={{ color: theme.muted, fontSize: "13px", marginTop: "3px" }}>
                            {workspace.activeDossier.ehpadNom || workspace.selectedEhpad?.nom} · Production du {formatDate(workspace.activeDossier.dateProduction)} · Mode {controleModeLabel(activeControleMode)}
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <button
                        type="button"
                        disabled={workspace.saving}
                        onClick={() => saveDossier("BROUILLON")}
                        style={{ ...saveDossierButtonStyle, opacity: workspace.saving ? 0.65 : 1 }}
                        title={`Enregistrer la fiche (${saveShortcutLabel})`}
                        aria-keyshortcuts={isAppleKeyboard ? "Meta+S" : "Control+S"}
                    >
                        <span>Enregistrer</span>
                        <span style={shortcutPillStyle}>{saveShortcutLabel}</span>
                    </button>
                    <button
                        type="button"
                        disabled={workspace.saving}
                        onClick={() => saveDossier("TERMINE")}
                        style={{ ...validateDossierButtonStyle, opacity: workspace.saving ? 0.65 : 1 }}
                    >
                        Valider la fiche
                    </button>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "8px", marginTop: "12px" }}>
                <input style={inputStyle} placeholder="Pharmacien" value={workspace.formData.meta.pharmacien || ""} onChange={e => updateMeta("pharmacien", e.target.value)} />
                <input style={inputStyle} placeholder="Heure de lancement" type="time" value={workspace.formData.meta.lancementHeure || ""} onChange={e => updateMeta("lancementHeure", e.target.value)} />
                <input style={inputStyle} placeholder="Nombre de sachets produits" value={workspace.formData.meta.nombreSachetsProduits || ""} onChange={e => updateMeta("nombreSachetsProduits", e.target.value)} />
                <input style={inputStyle} placeholder="Temps de production" value={workspace.formData.meta.tempsProduction || ""} onChange={e => updateMeta("tempsProduction", e.target.value)} />
                <input style={inputStyle} placeholder="Nombre de patients" value={workspace.formData.meta.nombrePatients || ""} onChange={e => updateMeta("nombrePatients", e.target.value)} />
                <input style={inputStyle} placeholder="Patient contrôlé pour libération" value={workspace.formData.meta.patientControle || ""} onChange={e => updateMeta("patientControle", e.target.value)} />
            </div>
        </div>
    );
}

function ClassicDossierTabs({ view }) {
    const { activeControleTab, controle, printControlePatientReport, setActiveControleTab, validatedPatientCount } = view;
    return (
        <div style={controleTabsRowStyle}>
            <div style={controleTabsStyle}>
                <button
                    type="button"
                    onClick={() => setActiveControleTab("controlePatient")}
                    title={`${validatedPatientCount} patient${validatedPatientCount > 1 ? "s" : ""} validé${validatedPatientCount > 1 ? "s" : ""} sur ${controle.patients.length} patient${controle.patients.length > 1 ? "s" : ""} à valider`}
                    style={controleTabButtonStyle(activeControleTab === "controlePatient")}
                >
                    <span>Contrôle patient</span>
                    <span style={controleTabBadgeStyle(activeControleTab === "controlePatient")}>
                        {validatedPatientCount}/{controle.patients.length}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setActiveControleTab("dossierLot")}
                    style={controleTabButtonStyle(activeControleTab === "dossierLot")}
                >
                    Dossier de lot
                </button>
            </div>
            <button
                type="button"
                onClick={printControlePatientReport}
                style={printReportButtonStyle}
            >
                Imprimer rapport
            </button>
        </div>
    );
}

function ClassicPointAction({ view, rowKey, pointColor }) {
    const { blockingPatientIssues, updateCommentaire, updatePatientControl, updatePatientLaunchStatus, workspace } = view;
    const commentaires = workspace.formData.commentaires || {};
    if (PATIENT_POINT_KEYS.includes(rowKey)) {
        return (
            <>
                <ControlPointInstruction text={PATIENT_CONTROL_INSTRUCTIONS[rowKey]} pointColor={pointColor} />
                <BlockingPatientIssuesForPoint
                    launchStatusLabels={VARIANT.launchStatusLabels}
                    issues={blockingPatientIssues.filter(issue => issue.pointKey === rowKey)}
                    onCommentChange={(patientId, pointKey, comment) => updatePatientControl(patientId, pointKey, { comment })}
                    onLaunchStatusChange={rowKey === "tracageLogiciels"
                        ? (patientId, pointKey, launchStatus) => updatePatientLaunchStatus(patientId, launchStatus)
                        : undefined}
                />
            </>
        );
    }
    if (rowKey === "importEureka") {
        return (
            <PointTwoComment
                value={commentaires.importEureka || ""}
                pointColor={pointColor}
                onChange={(value) => updateCommentaire("importEureka", value)}
            />
        );
    }
    if (rowKey === "rappelsProduction") {
        return (
            <PointMultiComments
                pointColor={pointColor}
                fields={RAPPELS_PRODUCTION_FIELDS}
                values={commentaires}
                onChange={updateCommentaire}
            />
        );
    }
    const comment = POINT_COMMENTS[rowKey];
    if (!comment) return null;
    return (
        <PointComment
            title={comment.title}
            value={commentaires[comment.key] || ""}
            pointColor={pointColor}
            onChange={(value) => updateCommentaire(comment.key, value)}
        />
    );
}

function ClassicSectionReport({ view, sectionKey }) {
    const { updateCommentaire, updateMeta, workspace } = view;
    const { formData, activeDossier } = workspace;
    const commentaire = key => formData.commentaires?.[key] || {};
    switch (sectionKey) {
        case "nonConformites":
            return (
                <NonConformityReport
                    meta={formData.meta || {}}
                    onMetaChange={updateMeta}
                    dateProduction={activeDossier.dateProduction}
                    value={commentaire("nonConformites")}
                    onChange={(value) => updateCommentaire("nonConformites", value)}
                />
            );
        case "controleProduction":
            return <ProductionControlReport value={commentaire("controleProduction")} onChange={(value) => updateCommentaire("controleProduction", value)} />;
        case "liberationLot":
            return (
                <LotReleaseReport
                    activeDossier={activeDossier}
                    selectedEhpad={workspace.selectedEhpad}
                    meta={formData.meta || {}}
                    value={commentaire("liberationLot")}
                    onChange={(value) => updateCommentaire("liberationLot", value)}
                />
            );
        case "rendement":
            return <YieldReport value={commentaire("rendement")} onChange={(value) => updateCommentaire("rendement", value)} />;
        case "extractionHp":
            return <ExtractionHpReport uploading={workspace.attachmentUploading} onUpload={workspace.uploadAttachments} />;
        default:
            return null;
    }
}

function ClassicDossierSection({ view, section, sectionIndex }) {
    return (
        <section style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={sectionTitleStyle}>
                <span style={sectionTitleMarkerStyle} />
                <span>{section.title}</span>
            </div>
            {REPORT_SECTIONS.includes(section.key) ? (
                <ClassicSectionReport view={view} sectionKey={section.key} />
            ) : section.rows.map(([rowKey, label], rowIndex) => {
                const pointNumber = controlPointNumber(VARIANT, sectionIndex, rowIndex);
                const pointColor = controlPointColor(pointNumber);
                return (
                    <ControlPointCard
                        key={rowKey}
                        number={pointNumber}
                        color={pointColor}
                        label={controlPointLabel(VARIANT, section.key, rowKey, label, view.activeControleMode)}
                        action={<ClassicPointAction view={view} rowKey={rowKey} pointColor={pointColor} />}
                    />
                );
            })}
        </section>
    );
}

export function renderActiveDossier(view) {
    const { activeControleTab, workspace } = view;
    if (!workspace.activeDossier) return renderDossierList(view);
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <ClassicDossierHeader view={view} />
            <ClassicDossierTabs view={view} />

            {activeControleTab === "controlePatient" && renderPatientControleTab(view)}

            {activeControleTab === "dossierLot" && SECTIONS.map((section, sectionIndex) => (
                <ClassicDossierSection key={section.key} view={view} section={section} sectionIndex={sectionIndex} />
            ))}

            {activeControleTab === "dossierLot" && (
                <ControleAttachments
                    dossierId={workspace.activeDossier.id}
                    attachments={workspace.formData.piecesJointes || []}
                    uploading={workspace.attachmentUploading}
                    onUpload={workspace.uploadAttachments}
                    onDelete={workspace.deleteAttachment}
                />
            )}
        </div>
    );
}
